/**
 * OpenAI Agents SDK Adapter — implements SdkAdapter for @openai/agents
 *
 * Uses the `run()` function (or `Runner`) from the OpenAI Agents SDK to execute
 * an Agent with instructions derived from the contract prompt.
 *
 * Supports:
 * - send()          — create a fresh Agent + run() and return finalOutput
 * - followUp()      — resume the same conversation via previousResponseId
 * - sendExecution() — rich path using full contract context
 *
 * The SDK's `run()` returns a RunResult with `finalOutput` (the model's final
 * text output) and `lastResponseId` for continuation.
 *
 * GuardrailHooks are mapped to the SDK's InputGuardrail system.
 */

import type {
  SdkAdapter,
  AdapterSendOptions,
  AgentExecutionRequest,
  AgentProgressEvent,
  CacheConfig,
  CandidateAgent,
} from "../lib/task-runner.js";
import type { ObserverEmitFn, TokenUsage } from "../lib/observability-bridge.js";
import {
  emitLlmTurnOpen,
  emitLlmTurnClose,
  emitToolExecOpen,
  emitToolExecClose,
} from "../lib/observability-bridge.js";
import type { GuardrailHooks } from "../lib/guardrail-hooks.js";
import type { MemoryRef } from "../lib/memory-store.js";
import type { LibraryToolRegistry } from "../lib/library-tool-binding.js";
import { buildOpenAIFunctionTools } from "../lib/library-tool-sdk.js";

// ---------------------------------------------------------------------------
// Duck-typed SDK types (avoid hard compile-time dependency on @openai/agents)
// ---------------------------------------------------------------------------

type RunResult = {
  finalOutput?: string | unknown;
  lastResponseId?: string;
  newItems?: Array<{
    type: string;
    rawItem?: { type?: string; content?: unknown };
    [key: string]: unknown;
  }>;
  state?: unknown;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
  };
};

type AgentOptions = {
  name: string;
  model?: string;
  instructions: string | ((ctx: unknown) => string);
  tools?: unknown[];
  inputGuardrails?: unknown[];
  outputType?: unknown;
  [key: string]: unknown;
};

type AgentInstance = AgentOptions & { [key: string]: unknown };

type RunOptions = {
  maxTurns?: number | null;
  signal?: AbortSignal;
  previousResponseId?: string;
  stream?: boolean;
  context?: unknown;
  [key: string]: unknown;
};

type AgentConstructor = new (options: AgentOptions) => AgentInstance;

type RunFn = (
  agent: AgentInstance,
  input: string | unknown[],
  options?: RunOptions,
) => Promise<RunResult>;

type StreamedRunResult = RunResult & {
  streamEvents(): AsyncIterable<RunStreamEvent>;
};

type RunStreamEvent = {
  type: string;
  name?: string;
  item?: {
    type: string;
    toolName?: string;
    toolInput?: unknown;
    agent?: { name?: string };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

type StreamRunFn = (
  agent: AgentInstance,
  input: string | unknown[],
  options?: RunOptions,
) => Promise<StreamedRunResult>;

// ---------------------------------------------------------------------------
// Guardrail bridge
// ---------------------------------------------------------------------------

function buildInputGuardrails(
  guardrails: GuardrailHooks,
): unknown[] {
  return [
    {
      name: "contract-runtime-guardrail",
      execute: async ({ input }: { input: unknown }) => {
        const inputStr = typeof input === "string"
          ? input
          : JSON.stringify(input);

        const results = guardrails.runChecks({ command: inputStr });
        const blocking = results.filter((r) => !r.passed && r.action === "block");

        return {
          outputInfo: { results },
          tripwireTriggered: blocking.length > 0,
        };
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// Input summary truncation
// ---------------------------------------------------------------------------

function truncateInputSummary(input: unknown, maxLen = 80): string {
  if (input == null) return "";
  let str: string;
  if (typeof input === "string") {
    str = input;
  } else {
    try { str = JSON.stringify(input); } catch { str = String(input); }
  }
  return str.length > maxLen ? str.slice(0, maxLen - 3) + "..." : str;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OpenAIAgentsSdkAdapterConfig {
  /** Model identifier (e.g. "gpt-4.1", "gpt-5.5"). Uses SDK default if omitted. */
  model?: string;
  /** Maximum conversation turns per run. Defaults to 10 (SDK default). */
  maxTurns?: number;
  /** Runtime guardrail hooks enforced via InputGuardrail. */
  guardrailHooks?: GuardrailHooks;
  /** Additional tools to pass to the Agent. */
  tools?: unknown[];
  /** Agent name prefix. Defaults to "contract-agent". */
  agentName?: string;
  /** AbortSignal for cancellation. */
  signal?: AbortSignal;
  /** Prompt caching configuration. Enabled by default. */
  cacheConfig?: CacheConfig;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export class OpenAIAgentsSdkAdapter implements SdkAdapter {
  private model: string | undefined;
  private maxTurns: number | undefined;
  private guardrailHooks: GuardrailHooks | undefined;
  private tools: unknown[] | undefined;
  private agentName: string;
  private signal: AbortSignal | undefined;
  private cacheConfig: CacheConfig;
  private lastResponseId: string | null = null;
  private lastAgent: AgentInstance | null = null;
  private lastMemoryRef: MemoryRef | null = null;
  private lastTokenUsage: TokenUsage | null = null;

  private AgentClass: AgentConstructor | null = null;
  private runFn: RunFn | null = null;
  private streamRunFn: StreamRunFn | null = null;

  constructor(config: OpenAIAgentsSdkAdapterConfig = {}) {
    this.model = config.model;
    this.maxTurns = config.maxTurns;
    this.guardrailHooks = config.guardrailHooks;
    this.tools = config.tools;
    this.agentName = config.agentName ?? "contract-agent";
    this.signal = config.signal;
    this.cacheConfig = config.cacheConfig ?? { enabled: true };
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  private async resolveSdk(): Promise<{ Agent: AgentConstructor; run: RunFn; streamRun?: StreamRunFn }> {
    if (this.AgentClass && this.runFn) {
      return { Agent: this.AgentClass, run: this.runFn, streamRun: this.streamRunFn ?? undefined };
    }

    const sdk = (await import("@openai/agents")) as unknown as {
      Agent: AgentConstructor;
      run: RunFn;
    };
    this.AgentClass = sdk.Agent;
    this.runFn = sdk.run;
    return { Agent: this.AgentClass, run: this.runFn };
  }

  private createAgent(
    Agent: AgentConstructor,
    instructions: string,
    _readonly: boolean,
    agents?: CandidateAgent[],
    extraTools?: unknown[],
  ): AgentInstance {
    const opts: AgentOptions = {
      name: this.agentName,
      instructions,
    };

    if (this.model) opts.model = this.model;
    const mergedTools = [
      ...(this.tools ?? []),
      ...(extraTools ?? []),
    ];
    if (mergedTools.length > 0) opts.tools = mergedTools;

    if (this.guardrailHooks) {
      opts.inputGuardrails = buildInputGuardrails(this.guardrailHooks);
    }

    // Register candidate sub-agents as handoffs so the main agent can delegate
    // to them (model B, LLM-internal routing). Each becomes its own Agent.
    if (agents && agents.length > 0) {
      opts.handoffs = agents.map((a) => {
        const subOpts: AgentOptions = { name: a.name, instructions: a.prompt };
        if (a.model) subOpts.model = a.model;
        if (a.tools) subOpts.tools = a.tools;
        return new Agent(subOpts);
      });
    }

    return new Agent(opts);
  }

  private buildRunOptions(previousResponseId?: string): RunOptions {
    const opts: RunOptions = {};

    if (this.maxTurns !== undefined) opts.maxTurns = this.maxTurns;
    if (this.signal) opts.signal = this.signal;
    if (previousResponseId) opts.previousResponseId = previousResponseId;

    return opts;
  }

  private extractOutput(result: RunResult): string {
    if (typeof result.finalOutput === "string") {
      return result.finalOutput;
    }

    if (result.finalOutput !== undefined && result.finalOutput !== null) {
      return JSON.stringify(result.finalOutput, null, 2);
    }

    if (result.newItems) {
      const textParts: string[] = [];
      for (const item of result.newItems) {
        if (item.type === "message_output_item" || item.type === "text") {
          const rawItem = item.rawItem as Record<string, unknown> | undefined;
          if (rawItem?.content) {
            const content = rawItem.content;
            if (Array.isArray(content)) {
              for (const part of content) {
                if (typeof part === "object" && part !== null && "text" in part) {
                  textParts.push((part as { text: string }).text);
                }
              }
            } else if (typeof content === "string") {
              textParts.push(content);
            }
          }
        }
      }
      if (textParts.length > 0) return textParts.join("\n");
    }

    return "";
  }

  private async runWithProgress(
    agent: AgentInstance,
    input: string,
    runOpts: RunOptions,
    onProgress?: (event: AgentProgressEvent) => void,
    observer?: ObserverEmitFn,
    parentSpanId?: string,
    agentSessionId?: string,
  ): Promise<RunResult> {
    const { run, streamRun } = await this.resolveSdk();

    if (!onProgress && !observer) {
      if (!streamRun) return run(agent, input, runOpts);
      const streamResult = await streamRun(agent, input, runOpts);
      for await (const _event of streamResult.streamEvents()) {
        // drain stream without processing
      }
      return streamResult;
    }

    if (!streamRun) {
      return run(agent, input, runOpts);
    }

    const streamResult = await streamRun(agent, input, runOpts);

    let turnNumber = 0;
    let currentTurnSpanId: string | undefined;
    let currentTurnStart = 0;
    let toolCallsInTurn = 0;
    let turnInputTokens = 0;
    let turnOutputTokens = 0;
    const openToolSpans: Array<{ spanId: string; toolName: string; startMs: number }> = [];

    const closePendingToolSpans = (): void => {
      for (const ts of openToolSpans) {
        emitToolExecClose(observer, ts.spanId, ts.toolName, Date.now() - ts.startMs, agentSessionId);
      }
      openToolSpans.length = 0;
    };

    const closeCurrentTurn = (): void => {
      if (currentTurnSpanId) {
        emitLlmTurnClose(observer, currentTurnSpanId, turnNumber, Date.now() - currentTurnStart, {
          inputTokens: turnInputTokens || undefined,
          outputTokens: turnOutputTokens || undefined,
          toolCallsCount: toolCallsInTurn || undefined,
        }, agentSessionId);
        currentTurnSpanId = undefined;
      }
    };

    for await (const event of streamResult.streamEvents()) {
      if (onProgress) emitProgressFromOpenAIEvent(event, onProgress);

      switch (event.type) {
        case "reasoning_item_created": {
          closePendingToolSpans();
          closeCurrentTurn();
          turnNumber++;
          toolCallsInTurn = 0;
          turnInputTokens = 0;
          turnOutputTokens = 0;
          currentTurnStart = Date.now();
          currentTurnSpanId = emitLlmTurnOpen(observer, parentSpanId, turnNumber, this.model, undefined, agentSessionId);
          break;
        }
        case "tool_called": {
          if (!currentTurnSpanId) {
            turnNumber++;
            toolCallsInTurn = 0;
            turnInputTokens = 0;
            turnOutputTokens = 0;
            currentTurnStart = Date.now();
            currentTurnSpanId = emitLlmTurnOpen(observer, parentSpanId, turnNumber, this.model, undefined, agentSessionId);
          }
          toolCallsInTurn++;
          const toolName = event.item?.toolName ?? "unknown";
          const summary = truncateInputSummary(event.item?.toolInput);
          const toolSpanId = emitToolExecOpen(observer, currentTurnSpanId, toolName, summary, turnNumber, agentSessionId);
          if (toolSpanId) {
            openToolSpans.push({ spanId: toolSpanId, toolName, startMs: Date.now() });
          }
          break;
        }
        case "tool_output": {
          if (openToolSpans.length > 0) {
            const ts = openToolSpans.shift()!;
            emitToolExecClose(observer, ts.spanId, ts.toolName, Date.now() - ts.startMs, agentSessionId);
          }
          break;
        }
        case "handoff_occurred": {
          closePendingToolSpans();
          closeCurrentTurn();
          break;
        }
        default:
          break;
      }
    }

    closePendingToolSpans();
    const sessionUsage = this.extractTokenUsage(streamResult);
    if (sessionUsage && currentTurnSpanId) {
      turnInputTokens = sessionUsage.inputTokens;
      turnOutputTokens = sessionUsage.outputTokens;
    }
    closeCurrentTurn();

    return streamResult;
  }

  // -------------------------------------------------------------------------
  // SdkAdapter interface
  // -------------------------------------------------------------------------

  private async resolveLibraryTools(
    libraryTools?: LibraryToolRegistry,
  ): Promise<unknown[]> {
    if (!libraryTools || libraryTools.getAll().length === 0) return [];
    try {
      return await buildOpenAIFunctionTools(libraryTools);
    } catch {
      return [];
    }
  }

  async send(
    prompt: string,
    options: AdapterSendOptions,
  ): Promise<string> {
    this.lastResponseId = null;
    this.lastAgent = null;
    this.lastMemoryRef = null;
    this.lastTokenUsage = null;

    const { Agent } = await this.resolveSdk();
    const split = options.splitPrompt;
    let instructions: string;
    let runInput: string;

    if (split && this.cacheConfig.enabled !== false) {
      instructions = split.system.join("\n\n---\n\n");
      runInput = split.user;
    } else {
      instructions = prompt;
      runInput = prompt;
    }

    const libTools = await this.resolveLibraryTools(options.libraryTools);
    const agent = this.createAgent(Agent, instructions, options.readonly, options.agents, libTools);
    const runOpts = this.buildRunOptions();
    const result = await this.runWithProgress(agent, runInput, runOpts, options.onProgress, options.observer, options.parentSpanId, options.sessionId);

    this.lastResponseId = result.lastResponseId ?? null;
    this.lastAgent = agent;
    this.lastTokenUsage = this.extractTokenUsage(result);
    this.lastMemoryRef = this.lastResponseId ? {
      id: this.lastResponseId,
      provider: "openai-responses",
      compat: "openai-agents-sdk@0.10",
      created_at: new Date().toISOString(),
    } : null;

    return this.extractOutput(result);
  }

  async followUp(message: string): Promise<string> {
    if (!this.lastResponseId) {
      throw new Error("followUp() called before send() — no active session");
    }

    const { run } = await this.resolveSdk();
    const agent = this.lastAgent!;
    const runOpts = this.buildRunOptions(this.lastResponseId);

    const result = await run(agent, message, runOpts);

    this.lastResponseId = result.lastResponseId ?? this.lastResponseId;

    return this.extractOutput(result);
  }

  async sendExecution(request: AgentExecutionRequest): Promise<string> {
    this.lastResponseId = null;
    this.lastAgent = null;
    this.lastMemoryRef = null;
    this.lastTokenUsage = null;

    const { Agent } = await this.resolveSdk();
    const split = request.splitPrompt;
    let instructions: string;
    let runInput: string;

    if (split && this.cacheConfig.enabled !== false) {
      instructions = split.system.join("\n\n---\n\n");
      runInput = split.user;
    } else {
      instructions = request.prompt;
      runInput = request.prompt;
    }

    const agents = request.options.agents ?? request.agents;
    const libTools = await this.resolveLibraryTools(request.options.libraryTools);
    const agent = this.createAgent(Agent, instructions, request.options.readonly, agents, libTools);
    const runOpts = this.buildRunOptions(request.memoryRef?.id);
    const result = await this.runWithProgress(agent, runInput, runOpts, request.options.onProgress, request.options.observer, request.options.parentSpanId, request.options.sessionId);

    this.lastResponseId = result.lastResponseId ?? null;
    this.lastAgent = agent;
    this.lastTokenUsage = this.extractTokenUsage(result);
    this.lastMemoryRef = this.lastResponseId ? {
      id: this.lastResponseId,
      provider: "openai-responses",
      compat: "openai-agents-sdk@0.10",
      created_at: new Date().toISOString(),
      parent_run_id: request.memoryRef?.id,
    } : null;

    return this.extractOutput(result);
  }

  getLastMemoryRef(): MemoryRef | null {
    return this.lastMemoryRef;
  }

  getLastTokenUsage(): TokenUsage | null {
    return this.lastTokenUsage;
  }

  isCompatible(compat: string): boolean {
    return compat.startsWith("openai-agents-sdk@");
  }

  private extractTokenUsage(result: RunResult): TokenUsage | null {
    const usage = result.usage;
    if (!usage) return null;
    if (typeof usage.input_tokens !== "number" && typeof usage.output_tokens !== "number") return null;
    const inputTokens = usage.input_tokens ?? 0;
    const outputTokens = usage.output_tokens ?? 0;
    return {
      inputTokens,
      outputTokens,
      totalTokens: usage.total_tokens ?? (inputTokens + outputTokens),
    };
  }

  // -------------------------------------------------------------------------
  // Test support
  // -------------------------------------------------------------------------

  /**
   * Inject custom Agent constructor and run function for testing.
   * Bypasses the dynamic import of @openai/agents.
   */
  static withRunFn(
    AgentClass: AgentConstructor,
    runFn: RunFn,
    config?: OpenAIAgentsSdkAdapterConfig,
    streamRunFn?: StreamRunFn,
  ): OpenAIAgentsSdkAdapter {
    const adapter = new OpenAIAgentsSdkAdapter(config);
    adapter.AgentClass = AgentClass;
    adapter.runFn = runFn;
    adapter.streamRunFn = streamRunFn ?? null;
    return adapter;
  }
}

// ---------------------------------------------------------------------------
// Progress event mapping
// ---------------------------------------------------------------------------

function emitProgressFromOpenAIEvent(
  event: RunStreamEvent,
  onProgress: (event: AgentProgressEvent) => void,
): void {
  switch (event.type) {
    case "tool_called":
      if (event.item?.toolName) {
        onProgress({ type: "tool_use", tool_name: event.item.toolName });
      }
      break;
    case "tool_output":
      onProgress({ type: "tool_result" });
      break;
    case "reasoning_item_created":
      onProgress({ type: "text", message: "Reasoning..." });
      break;
    case "handoff_occurred":
      if (event.item?.agent?.name) {
        onProgress({ type: "status", message: `Handoff to ${event.item.agent.name}` });
      }
      break;
    default:
      break;
  }
}
